import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { missingSizeSelection } from '@theme/product-form';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * The main button's label for the variant being selected, read before product-form.js morphs it.
   * @type {string | undefined}
   */
  #pendingButtonLabel;

  /** @returns {'top' | 'bottom'} */
  get #position() {
    return this.dataset.position === 'top' ? 'top' : 'bottom';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    this.#setupIntersectionObserver();
    this.#applyButtonLabel(this.#readButtonLabel(this.#getProductForm()?.querySelector('[ref="addToCartButton"]')));

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    // Capture phase so this runs before the section-level listeners (including product-form.js), and its
    // promise callback is registered first - see #captureButtonLabel.
    document.addEventListener(StandardEvents.productSelect, this.#captureButtonLabel, { capture: true, signal });
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController?.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    // Avoid leaving scroll-margin-block permanently reserving space for a bar that's gone.
    if (this.#isStuck) {
      document.body.style.setProperty(this.#heightCustomProperty, '0px');
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    // Top-position bars don't need the footer observer — only bail if bottom position has no footer
    if (!footer && this.#position === 'bottom') return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Footer observer: bottom position only — top position stays visible at page bottom
    if (this.#position === 'bottom') {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            // Footer out of view - check if we should show sticky bar again
            const rect = buyButtonsBlock.getBoundingClientRect();
            // Only show if buy buttons are fully above the viewport (scrolled past)
            if (rect.bottom < 0) {
              this.#hiddenByBottom = false;
              if (!this.#isChatActive()) {
                this.#showStickyBar();
              }
            }
          }
        },
        {
          // Pre-hides the bar 200px before the footer enters the viewport so the
          // slide-out animation completes before the footer visually overlaps the bar.
          rootMargin: '200px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // A size hasn't been chosen yet: forward the click so the real form's own validation
    // shows the error and focuses/scrolls to the Size control, but skip the optimistic
    // "added" animation below since nothing is actually being added to the cart.
    if (missingSizeSelection(this.#targetAddToCartButton)) {
      this.#targetAddToCartButton.click();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const { addToCartButton, productImage } = this.refs;

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !productImage) {
      // No fly-to-cart animation — reset button state after short delay
      this.#resetTimeout = setTimeout(() => addToCartButton.removeAttribute('data-added'), 800);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => addToCartButton.removeAttribute('data-added'), 800);
  };

  /**
   * Reads the main button's label out of the variant response before product-form.js gets to it.
   * product-form.js morphs the real button from this same parsed document, which moves the label
   * nodes out of it, so reading later (in #handleProductSelect) can find an empty button.
   * @param {ProductSelectEvent} event - The product select event
   */
  #captureButtonLabel = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        this.#pendingButtonLabel = this.#readButtonLabel(
          detail.html.querySelector('product-form-component [ref="addToCartButton"]')
        );
      })
      .catch(() => {
        // Aborted or failed fetches are already reported by #handleProductSelect.
      });
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant, mainMorphed } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Combined-listing colour swaps morph the entire <main> (variant-picker's updateMain), which
        // already updates this bar in place and *moves* its nodes out of the parsed `html` - including
        // the main button's label, so #pendingButtonLabel can be empty too. Morphing again from that
        // stale document would strip the button label and quantity display, so re-sync from the live DOM.
        if (mainMorphed) {
          this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
          this.#applyButtonLabel(this.#readButtonLabel(this.#targetAddToCartButton));
          this.#pendingButtonLabel = undefined;
          if (variant == null) this.#handleVariantUnavailable();
          this.#updateButtonText();
          return;
        }

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        this.#applyButtonLabel(this.#pendingButtonLabel);
        this.#pendingButtonLabel = undefined;

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.removeAttribute('data-puppet');
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * The custom property scroll-margin-block reads to keep a scrollIntoView() target clear of
   * this bar - top and bottom positions occupy opposite edges of the viewport, so each needs
   * its own property (see snippets/variant-picker-styles.liquid).
   * @returns {string}
   */
  get #heightCustomProperty() {
    return this.#position === 'top' ? '--sticky-add-to-cart-height-top' : '--sticky-add-to-cart-height-bottom';
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    document.body.style.setProperty(this.#heightCustomProperty, `${stickyBar.offsetHeight}px`);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    document.body.style.setProperty(this.#heightCustomProperty, '0px');
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Finds a ref'd element in the live DOM. Use instead of this.refs for reads that can run straight
   * after morph(): refs are rebuilt asynchronously by a MutationObserver, so until it fires they can
   * point at elements morph has since replaced.
   * Not in the Refs typedef on purpose, as `addToCartLabel` is only ever read through here.
   * @param {string} refName
   * @returns {HTMLElement | null}
   */
  #queryRef(refName) {
    return this.querySelector(`[ref="${refName}"]`);
  }

  /**
   * Reads the label text from the main product form's add to cart button.
   * @param {Element | null | undefined} sourceButton - The main form's add to cart button
   * @returns {string | undefined}
   */
  #readButtonLabel(sourceButton) {
    return sourceButton?.querySelector('.add-to-cart-text__content')?.textContent?.trim() || undefined;
  }

  /**
   * Mirrors the main product form button's label onto the sticky button, so merchant
   * overrides set on the add-to-cart block (e.g. "Pre-Order") show in both places.
   * @param {string | undefined} label
   */
  #applyButtonLabel(label) {
    const addToCartLabel = this.#queryRef('addToCartLabel');
    if (!addToCartLabel || !label) return;

    addToCartLabel.textContent = label;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const addToCartButton = /** @type {HTMLButtonElement | null} */ (this.#queryRef('addToCartButton'));
    const quantityDisplay = this.#queryRef('quantityDisplay');
    const quantityNumber = this.#queryRef('quantityNumber');
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
